import type { ReactNode } from 'react'
import { cn } from '@/lib/utils'
import type { SortDir } from '@/lib/useSortable'

/**
 * Clickable column label with a sort caret, for use inside a `<th>`. The `<th>`
 * keeps its own styling (sticky, padding, text alignment) — drop this in as its
 * content. A faint caret appears on hover for inactive columns; the active
 * column shows a solid ▲ / ▼. Pair with the `useSortable` hook.
 */
export function SortHeader({
  label,
  sortKey,
  active,
  dir,
  onSort,
  align = 'right',
}: {
  label: ReactNode
  sortKey: string
  active: boolean
  dir: SortDir
  onSort: (key: string) => void
  align?: 'left' | 'right'
}) {
  return (
    <button
      type="button"
      onClick={() => onSort(sortKey)}
      aria-sort={active ? (dir === 'asc' ? 'ascending' : 'descending') : undefined}
      className={cn(
        'group inline-flex cursor-pointer select-none items-center gap-1 hover:text-stone-600 dark:hover:text-stone-300',
        align === 'right' && 'flex-row-reverse',
        active && 'text-stone-600 dark:text-stone-300',
      )}
    >
      <span>{label}</span>
      <span
        className={cn(
          'text-[8px] leading-none transition-opacity',
          active ? 'opacity-100' : 'opacity-0 group-hover:opacity-50',
        )}
        aria-hidden
      >
        {active ? (dir === 'asc' ? '▲' : '▼') : '▼'}
      </span>
    </button>
  )
}
